import { faUnlockAlt } from "@fortawesome/pro-regular-svg-icons/faUnlockAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Dialog, DialogActions, DialogContent, FormGroup, TextField } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import AuthService, { AuthenticationOptions } from "api/Auth";
import * as api from "api/Core";
import { FormStyleClasses } from "assets/jss/common-styles";
import { RequestIndicator } from "components/CallServer";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import MuiCompareTextField from "components/MuiExtras/CustomInput/MuiCompareTextField";
import MuiPasswordTextField from "components/MuiExtras/CustomInput/MuiPasswordTextField";
import { GridEx } from "components/MuiExtras/Grid/GridEx";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiDialogTitleClose from "components/MuiExtras/MuiDialogTitleClose";
import MuiPushRight from "components/MuiExtras/MuiPushRight";
import ReactComment from "components/ReactComment";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { ChangePasswordResult } from "models/ApiEnums";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import Session from "services/Session";

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    userId: string;
    requiresOldPassword: boolean;
    onClose: () => void;
}

interface IState {
    formCycle: RequestLifeCycle,

    oldPassword: string;
    password: string;
    confirmPassword: string;
    passwordRule: boolean;
    lastError: any;
}

type Props = IProps & WithStyles<typeof FormStyleClasses> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class ChangePasswordDialog extends React.Component<Props, IState> {

    private authOptions: AuthenticationOptions = null;
    private authService = new AuthService();

    ///
    constructor(props: Props) {
        super(props);
        this.state = {
            formCycle: RequestLifeCycle.Init,
            oldPassword: "",
            password: "",
            confirmPassword: "",
            passwordRule: false,
            lastError: null
        };
    }

    ///
    private res(r: string) {
        return this.props.t("Views.User.Dialogs.ChangePassword." + r);
    }

    ///
    private initialLoad() {
        this.authService.getSettings()
            .then((settings) => {
                this.authOptions = settings.authOptions;
                this.setState({
                    formCycle: RequestLifeCycle.InitSuccess
                });
            })
            .catch((err: any) => {
                this.setState({
                    formCycle: RequestLifeCycle.InitFailed
                });
            });
    }

    ///
    private isValidForm(): boolean {
        if (this.props.requiresOldPassword && this.state.oldPassword.length == 0)
            return false;

        return this.state.passwordRule && this.state.password === this.state.confirmPassword;
    }

    ///
    private renderForm() {
        return (
            <>
                {/* Truque para impedir autocomplete */}
                <ReactComment text="<input type='password' autocomplete='new-password' />" />

                {
                    this.props.requiresOldPassword &&
                    <FormGroup>
                        <TextField
                            label={this.res("CurrentPasswordLabel")}
                            type="password"
                            autoFocus={this.props.requiresOldPassword}
                            fullWidth={true}
                            autoComplete="off"
                            value={this.state.oldPassword}
                            inputProps={{ maxLength: 16 }}
                            onChange={
                                (e) => {
                                    this.setState({ oldPassword: e.target.value });
                                }
                            }
                        />
                    </FormGroup>
                }

                <FormGroup>
                    <MuiPasswordTextField
                        label={this.res("NewPasswordLabel")}
                        fullWidth={true}
                        autoFocus={!this.props.requiresOldPassword}
                        maxLength={16}
                        minLength={this.authOptions.policies.passwordMinLength}
                        minNumericChars={this.authOptions.policies.passwordMinNumericChars}
                        minSpecialChars={this.authOptions.policies.passwordMinSpecialChars}
                        minLowerChars={this.authOptions.policies.passwordMinLowerChars}
                        minUpperChars={this.authOptions.policies.passwordMinUpperChars}
                        autoComplete="off"
                        value={this.state.password}
                        onChange={
                            (text, isValid) => {
                                this.setState(
                                    {
                                        password: text,
                                        passwordRule: isValid,
                                    }
                                );
                            }
                        }
                    />
                </FormGroup>

                <FormGroup>
                    <MuiCompareTextField
                        label={this.res("ConfirmNewPasswordLabel")}
                        fullWidth={true}
                        maxLength={16}
                        valueToCompare={this.state.password}
                        onChange={
                            (text) => {
                                this.setState({ confirmPassword: text });
                            }
                        }
                    />
                </FormGroup>

            </>
        );
    }

    private handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        if (this.isValidForm()) {

            this.setState({
                formCycle: RequestLifeCycle.Posting,
            });

            api.changeAccountPassword(this.props.userId, this.state.oldPassword, this.state.password)
                .then(() => {
                    this.setState({ formCycle: RequestLifeCycle.None });
                    this.props.snackbar.showMessage(this.res("SuccessMessage"), "success");
                    this.props.onClose();
                })
                .catch((error: any) => {
                    this.setState({ formCycle: RequestLifeCycle.None, lastError: error });
                });
        }
    }

    ///
    componentDidMount() {
        if (this.state.formCycle == RequestLifeCycle.Init) {
            this.initialLoad();
        }
    }

    ///
    render() {

        return (
            <Dialog
                open
                fullWidth
                maxWidth="sm"
                onClose={
                    (event, reason) => {
                        if (reason !== 'backdropClick') {
                            this.props.onClose();
                        }
                    }
                }
            >
                <MuiDialogTitleClose onClose={this.props.onClose}>
                    <span>{this.res("HeaderTitle")}</span>
                </MuiDialogTitleClose>

                <DialogContent>

                    {this.state.formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                    {
                        this.state.formCycle == RequestLifeCycle.Init
                            ? <RequestIndicator />
                            : (
                                <>
                                    {
                                        this.state.lastError &&
                                        <MuiContainer>
                                            <RequestErrorReport error={this.state.lastError} />
                                        </MuiContainer>
                                    }

                                    <form noValidate autoComplete="nope" >

                                        <GridEx container spacing={2}>
                                            <GridEx item >
                                                <FontAwesomeIcon icon={faUnlockAlt} size="6x" />
                                            </GridEx>

                                            <GridEx item xs>
                                                {this.renderForm()}
                                            </GridEx>
                                        </GridEx>

                                    </form>
                                </>
                            )
                    }

                </DialogContent>

                <DialogActions>
                    <MuiPushRight>
                        <Button
                            color="primary"
                            size="large"
                            variant="contained"
                            component="span"
                            disabled={!this.isValidForm()}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.handleSubmit(e)}
                        >
                            <span>{this.props.t("Common.Buttons.ConfirmText")}</span>
                        </Button>
                    </MuiPushRight>
                </DialogActions>
            </Dialog >
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(FormStyleClasses)(ChangePasswordDialog)));